import { takeEvery, fork, call, put, select } from 'redux-saga/effects';
import {
    Product,
    ProductCode,
    PaymentProviderType,
    TransactionType,
    ProductType,
    AccountCore,
    AccessLevel,
} from '@dev/ee-api/ee-api-models';
import { sendEventToGTM } from '@dev/ee-tools';
import { Logger } from '@api/services/errors/appLogger';

import { getProductsList, getBillingPlanList, changeProductPost } from '@api/services/ApiCategories/products';
import { ProductActionTypes, DashboardProduct, LoadBillingPlans, ChangeProductAction } from '@api/store/products/types';
import { ProductListLoaded, ProductListLoadingError, PlansLoaded, PlansLoadingError } from '@api/store/products/actions';
import { ShowToast } from '@api/store/toast/actions';
import { DashboardPaths } from '@api/services/dictionaries/paths';
import { errorToast } from './commonGenerators/errorToast';
import { HideDialog, UpdateDialog } from '@api/store/dialog/actions';
import { getAccessLevels, getGeneralInfo } from '@api/store/account/actions';

const getProductUrl = (code: ProductCode): string => {
    switch (code) {
        case ProductCode.EmailApi:
            return '/api';
        case ProductCode.EmailVerification:
            return '/evs';
        case ProductCode.Marketing:
            return '/marketing';
        default:
            return '/404';
    }
};

function* getProducts(): any {
    try {
        const accessLevels = yield select(getAccessLevels);
        if (!accessLevels?.includes(AccessLevel.ViewBilling)) return yield put(ProductListLoaded([]));

        const resp = yield call(getProductsList);
        const products = resp.data.map((product: Product): DashboardProduct => {
            return {
                ...product,
                ...{
                    dashboardURL: getProductUrl(product.Code),
                },
            };
        });
        const dashboards = products.filter((product: Product) => !product.IsAddon);

        yield put(ProductListLoaded(dashboards));
    } catch (e) {
        yield put(ProductListLoadingError());
        Logger.error(e);
        const errorMsg = e?.response?.data?.Error ? e.response.data.Error : e.message;
        yield put(
            ShowToast({
                message: 'Error: ' + errorMsg,
                severity: 'error',
                duration: 6000,
            })
        );
    }
}

function* getBillingPlans(action: LoadBillingPlans): any {
    const { payload } = action;
    const { IsSub, AccountAccessLevel }: AccountCore = yield select(getGeneralInfo);

    if (IsSub || !AccountAccessLevel?.includes(AccessLevel.ViewBilling)) {
        return yield put(PlansLoaded([]));
    }

    try {
        const resp = yield call(getBillingPlanList, payload.billingPlanCode);
        yield put(PlansLoaded(resp.data));
    } catch (e) {
        yield put(PlansLoadingError());
        yield call(errorToast, e);
    }
}

function* changeProductToMarketing(action: ChangeProductAction): any {
    const { billingPlanID, defaultTier } = action.payload;
    yield put(UpdateDialog({ isLoading: true }));
    const changeProductParams = {
        ProviderType: PaymentProviderType.None,
        ProviderOperation: TransactionType.SwitchTrial,
        PlanToPurchase: {
            BillingPlanID: billingPlanID,
            Amount: 0,
            BillingPlanTierID: defaultTier,
        },
    };

    try {
        yield call(changeProductPost, changeProductParams);
        const eventName = `Switch to: Marketing`;
        sendEventToGTM(eventName, { category: `${ProductType.API}-dashboard-product-change` });

        yield put(HideDialog());
        window.location.replace(`${location.origin}${DashboardPaths[ProductType.Marketing]}`);
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* watchGetProducts(): any {
    yield takeEvery(ProductActionTypes.LOAD_PRODUCT_LIST, getProducts);
}

function* watchGetBillingPlan(): any {
    yield takeEvery(ProductActionTypes.LOAD_BILLING_PLANS, getBillingPlans);
}

function* watchChangeProduct(): any {
    yield takeEvery(ProductActionTypes.CHANGE_PRODUCT, changeProductToMarketing);
}

const ProductSagas = [fork(watchGetProducts), fork(watchGetBillingPlan), fork(watchChangeProduct)];

export default ProductSagas;
